import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Type definitions for the E53 status data
export interface QuarterData {
  ppo: string;
  date: string;
  version: number | string;
  status: string;
}

export interface E53StatusItem {
  ppo: string;
  year: string | number;
  version?: string | number;
  status?: string;
  closed?: boolean;
  quarters: {
    q1?: QuarterData;
    q2?: QuarterData;
    q3?: QuarterData;
    q4?: QuarterData;
  };
}

export interface E53StatusResponse {
  inbound: E53StatusItem[];
  outbound: E53StatusItem[];
}

// Parameters for filtering the status data
export interface E53StatusParams {
  year?: string;
  ppo?: string;
  inbound?: boolean;
  open?: boolean;
}

// Query key for consistent reference
export const e53StatusQueryKey = ['e53-status'];

/**
 * Fetches E53 status data from the API
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Promise with the E53 status data
 */
const fetchE53Status = async (params?: E53StatusParams): Promise<E53StatusResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params?.year) {
      queryParams.append('year', params.year);
    }
    
    if (params?.ppo) {
      queryParams.append('ppo', params.ppo);
    }
    
    if (params?.inbound !== undefined) {
      queryParams.append('inbound', params.inbound.toString());
    }
    
    if (params?.open !== undefined) {
      queryParams.append('open', params.open.toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/data-set/e53/status${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching E53 status data:', error);
    throw error;
  }
};

/**
 * Hook for fetching and managing E53 status data
 * @param params - Optional parameters for filtering data by year and PPO
 * @returns Query result with E53 status data, loading state, error state, and refetch function
 */
export function useE53Status(params?: E53StatusParams) {
  return useQuery<E53StatusResponse>({
    queryKey: [...e53StatusQueryKey, params],
    queryFn: () => fetchE53Status(params),
    retry: 2,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });
} 